import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-teal-hover shadow-sm hover:shadow-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent hover:bg-secondary hover:border-primary text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Teal brand CTA
        teal: "bg-teal text-white font-semibold hover:bg-teal-hover shadow-sm hover:shadow-md",
        // Athlete green CTA
        "athlete-green": "bg-athlete-green text-white font-semibold hover:bg-athlete-green-hover shadow-sm hover:shadow-md",
        // Enterprise
        enterprise: "bg-enterprise-teal text-white font-semibold hover:bg-enterprise-teal-hover shadow-sm hover:shadow-md",
        // Outline teal
        "outline-teal": "border border-border bg-transparent text-foreground hover:border-teal hover:text-teal",
        // Legacy variants mapped to new colors
        electric: "bg-teal text-white font-semibold hover:bg-teal-hover shadow-sm hover:shadow-md",
        navy: "bg-navy text-white font-semibold hover:bg-navy-light shadow-sm hover:shadow-md",
        hero: "bg-transparent text-foreground border border-border hover:border-teal hover:text-teal",
        "hero-electric": "bg-teal text-white font-semibold hover:bg-teal-hover shadow-sm hover:shadow-md",
        "outline-electric": "border border-teal text-teal bg-transparent hover:bg-teal hover:text-white",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };